import { relations, sql } from 'drizzle-orm'
import {
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import { phoneNumberColumn } from '../common/customTypes'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { users } from './user'

// Profile behind the {{sender…}} merge tags, one row per staff user who signs
// newsletters. Kept off the users table, which is mostly customers and
// contacts. publicPhoneNumber is the sales number shown to recipients; the
// user's own phoneNumber may be personal and is never used. photoUrl is a
// public S3 URL from the newsletter image uploader.
export const newsletterSenderProfiles = pgTable(
  'newsletter_sender_profiles',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id).unique(),
    jobTitle: text(),
    publicPhoneNumber: phoneNumberColumn(),
    linkedinUrl: text(),
    photoUrl: text(),
  },
  (table) => getBaseIndexes('newsletter_sender_profiles', table),
)

export const newsletterStatusEnum = pgEnum('newsletter_status', [
  'draft',
  'sending',
  'sent',
])

// 'newsletter' rows are sent once to an audience. 'automation' rows are
// templates the backend renders per recipient when an event fires (e.g. a
// technician's service recommendation); they have no audience and no send
// button, and are looked up by `automationKey`.
export const newsletterKindEnum = pgEnum('newsletter_kind', [
  'newsletter',
  'automation',
])

export interface NewsletterAutomationSettings {
  enabled: boolean
  // Days between the triggering event and the send.
  delayDays: number
  // Personal voucher minted per send: gross euro value and validity from send.
  voucherValue: number
  voucherValidDays: number
  // Days before the voucher expires to send the reminder; 0 = no reminder.
  reminderDaysBefore: number
  // Where "Varaa aika" points; required to enable a service template.
  bookingUrl?: string
}

export const newsletters = pgTable(
  'newsletters',
  {
    ...getBaseFields(),
    subject: text().notNull(),
    // Staff-only label to tell near-identical newsletters apart in the list
    // (e.g. same subject, different audience). Never shown to recipients.
    internalName: text(),
    designJson: jsonb(),
    html: text(),
    status: newsletterStatusEnum().notNull().default('draft'),
    audienceFilter: jsonb(),
    recipientCount: integer(),
    sentAt: getTimestampWithTimezone(),
    sentByUserId: getNullableIdReference(() => users.id),
    // Personal sender: From display name and Reply-To of this staff user.
    // Null sends as the brand (noreply@newsletter…, Reply-To info@).
    senderUserId: getNullableIdReference(() => users.id),
    kind: newsletterKindEnum().notNull().default('newsletter'),
    automationKey: text(),
    automationSettings: typedJsonb<NewsletterAutomationSettings>(
      'automation_settings',
    ),
  },
  (table) => [
    ...getBaseIndexes('newsletters', table),
    // One live template per automation key, so the sender never has to pick.
    uniqueIndex('newsletters_automation_key_unique_idx')
      .on(table.automationKey)
      .where(sql`${table.deletedAt} IS NULL`),
  ],
)

export const newslettersRelations = relations(newsletters, ({ one }) => ({
  sentByUser: one(users, {
    fields: [newsletters.sentByUserId],
    references: [users.id],
  }),
  senderUser: one(users, {
    fields: [newsletters.senderUserId],
    references: [users.id],
    relationName: 'sender',
  }),
}))
